import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ApiService from '../services/api.service';
import {
  invalidateAllRequestViews,
  invalidateRequestViewsAndBadge,
} from '../hooks/supportRequestInvalidation';

/**
 * True while the support-request event stream is delivering.
 *
 * The polling hooks read this to decide whether they need to poll at all — the
 * timer is the fallback for when the stream is down, not a second mechanism
 * running alongside it.
 */
const SupportRequestStreamContext = createContext(false);

export function useSupportRequestStreamConnected(): boolean {
  return useContext(SupportRequestStreamContext);
}

/** A planned close (the server caps stream lifetime) — reconnect straight away. */
const RECONNECT_MS = 500;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

/** Exponential with jitter, so a server restart doesn't bring every tab back at once. */
function backoffMs(failures: number): number {
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (failures - 1));
  return base * (0.8 + Math.random() * 0.4);
}

interface SupportRequestStreamProviderProps {
  /** False for users who can't see requests at all — no point holding a connection. */
  enabled: boolean;
  children: ReactNode;
}

/**
 * Holds one SSE connection for the whole app and turns each frame into a React
 * Query invalidation.
 *
 * Deliberately mounted once, at the root, rather than per thread view. Browsers
 * cap HTTP/1.1 at six connections per origin: behind the ALB (which serves the
 * browser over h2) that doesn't bite, but local dev is plain HTTP/1.1 and a
 * per-component stream would starve the rest of the app with a few tabs open.
 *
 * The stream stays open in a background tab. That's the point of it — a reply
 * can land while the user is on another page, and unlike a poll it costs
 * nothing while idle.
 *
 * Embedded views are out of scope by decision, not by oversight: `Embed.tsx`
 * doesn't route through `AuthenticatedRootPage`, so an embed never mounts this
 * and its request views stay on the fallback poll — the behaviour they already
 * had. Don't mount it there without deciding that embed sessions should hold a
 * long-lived connection.
 */
export function SupportRequestStreamProvider({
  enabled,
  children,
}: SupportRequestStreamProviderProps) {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);

  // The effect below must not re-run when `connected` flips, or setting the
  // state would tear down the very stream that set it.
  const connectedRef = useRef(false);
  const markConnected = (value: boolean) => {
    if (connectedRef.current === value) return;
    connectedRef.current = value;
    setConnected(value);
  };

  useEffect(() => {
    if (!enabled) {
      markConnected(false);
      return;
    }

    let cancelled = false;
    let controller: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;

    const connect = async () => {
      if (cancelled) return;
      controller = new AbortController();
      // Either end of the connection can call it a failure: a thrown fetch on
      // this side, or an `error` frame from a server whose change detection
      // stalled. Both back off; a clean close does not.
      let failed = false;

      try {
        const stream = ApiService.getInstance().streamGet(
          '/requests/events',
          controller.signal
        );

        for await (const event of stream) {
          if (cancelled) return;

          switch (event?.type) {
            case 'ready':
              failures = 0;
              markConnected(true);
              // Anything that changed while we were disconnected is picked up
              // here rather than replayed by the server.
              invalidateAllRequestViews(queryClient);
              break;
            case 'request_changed':
            case 'request_removed':
              if (event.id) invalidateRequestViewsAndBadge(queryClient, event.id);
              break;
            case 'error':
              // The stream gave up on its own — reopening immediately would
              // just fail again.
              failed = true;
              break;
            case 'reconnect':
              // Planned close: the server caps stream lifetime. The loop ends
              // on its own and we reopen straight away.
              break;
            default:
              // Unknown frame types are ignored on purpose, so the server can
              // add one without waiting for every client to catch up.
              break;
          }
        }
      } catch (err) {
        if (cancelled) return;
        failed = true;
      }

      if (cancelled) return;
      markConnected(false);

      if (failed) {
        // Includes a 401, which is worth retrying: the token may be mid-refresh.
        failures += 1;
        timer = setTimeout(connect, backoffMs(failures));
      } else {
        timer = setTimeout(connect, RECONNECT_MS);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
      markConnected(false);
    };
  }, [enabled, queryClient]);

  return (
    <SupportRequestStreamContext.Provider value={connected}>
      {children}
    </SupportRequestStreamContext.Provider>
  );
}
